import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClockIcon,
  UserIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, formatDate, cn } from '../utils';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import CarForm from '../components/CarForm';

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isCarFormOpen, setIsCarFormOpen] = useState(false);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [documentMetadata, setDocumentMetadata] = useState([]);
  
  // Work logs state
  const [workLogs, setWorkLogs] = useState([]);
  const [workLogsSummary, setWorkLogsSummary] = useState({});
  const [showAddWorkLog, setShowAddWorkLog] = useState(false);
  const [newWorkLog, setNewWorkLog] = useState({
    workType: 'maintenance',
    description: '',
    hoursWorked: 0,
    laborCost: 0,
    totalCost: 0,
    notes: '',
    partsUsed: []
  });

  const documentTypes = [
    { value: 'registration', label: 'Registration' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'service', label: 'Service Record' },
    { value: 'warranty', label: 'Warranty' },
    { value: 'manual', label: 'Manual' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'other', label: 'Other' }
  ];

  const workTypes = [
    { value: 'service', label: 'Service' },
    { value: 'repair', label: 'Repair' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'diagnostic', label: 'Diagnostic' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchCarDetails();
    fetchWorkLogs();
  }, [id]);

  const fetchCarDetails = async () => {
    try {
      const response = await apiService.getCar(id);
      if (response.success) {
        setCar(response.car);
        setDocuments(response.car.documents || []);
      }
    } catch (error) {
      toast.error(error.message);
      navigate('/cars');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkLogs = async () => {
    try {
      const response = await apiService.getCarWorkLogs(id);
      if (response.success) {
        setWorkLogs(response.workLogs || []);
        setWorkLogsSummary(response.summary || {});
      }
    } catch (error) {
      console.error('Failed to fetch work logs:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setDocumentMetadata(files.map(() => ({ name: '', type: 'other', description: '' })));
  };

  const updateDocumentMetadata = (index, field, value) => {
    setDocumentMetadata(prev => 
      prev.map((meta, i) => i === index ? { ...meta, [field]: value } : meta)
    );
  };

  const uploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploadingDocs(true);
    try {
      const response = await apiService.uploadCarDocuments(id, selectedFiles, documentMetadata);
      setDocuments(prev => [...prev, ...response.documents]);
      setSelectedFiles([]);
      setDocumentMetadata([]);
      toast.success('Documents uploaded successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingDocs(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await apiService.deleteCarDocument(id, documentId);
      setDocuments(prev => prev.filter(doc => doc._id !== documentId));
      toast.success('Document deleted successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const addWorkLog = async () => {
    if (!newWorkLog.description.trim()) {
      toast.error('Please provide a description for the work log');
      return;
    }

    try {
      const response = await apiService.addCarWorkLog(id, newWorkLog);
      setWorkLogs(prev => [response.workLog, ...prev]);
      setWorkLogsSummary(response.summary);
      setNewWorkLog({
        workType: 'maintenance',
        description: '',
        hoursWorked: 0,
        laborCost: 0,
        totalCost: 0,
        notes: '',
        partsUsed: []
      });
      setShowAddWorkLog(false);
      toast.success('Work log added successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCarSaved = (updatedCar) => {
    setCar(updatedCar);
    setIsCarFormOpen(false);
  };

  const getMaintenanceStatus = () => {
    if (!car) return { status: 'unknown', color: 'text-gray-600 bg-gray-100', text: 'Unknown' };

    const now = new Date();
    const lastService = car.lastServiceDate ? new Date(car.lastServiceDate) : null;
    const nextService = car.nextServiceDate ? new Date(car.nextServiceDate) : null;

    if (nextService && now > nextService) {
      return { status: 'overdue', color: 'text-red-600 bg-red-100', text: 'Overdue' };
    } else if (nextService && (nextService.getTime() - now.getTime()) / (1000 * 60 * 60 * 24) <= 7) {
      return { status: 'due', color: 'text-yellow-600 bg-yellow-100', text: 'Due Soon' };
    } else if (lastService) {
      return { status: 'current', color: 'text-green-600 bg-green-100', text: 'Current' };
    } else {
      return { status: 'unknown', color: 'text-gray-600 bg-gray-100', text: 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-300">Car not found</p>
        <button
          onClick={() => navigate('/cars')}
          className="mt-4 btn btn-primary"
        >
          Back to Cars
        </button>
      </div>
    );
  }

  const maintenanceStatus = getMaintenanceStatus();
  const primaryImage = car.images?.find(img => img.isPrimary) || car.images?.[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cars')}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{car.numberPlate}</h1>
            <p className="text-dark-300">{car.make} {car.model} ({car.year})</p>
          </div>
        </div>
        
        {isAdmin && (
          <button
            onClick={() => setIsCarFormOpen(true)}
            className="btn btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit Car
          </button>
        )}
      </div>

      {/* Car Image and Basic Info */}
      <div className="bg-dark-800 rounded-lg overflow-hidden">
        <div className="md:flex">
          {primaryImage && (
            <div className="md:w-1/3">
              <img
                src={primaryImage.url}
                alt={`${car.make} ${car.model}`}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
          )}
          
          <div className="md:w-2/3 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">{car.make} {car.model}</h2>
              <span className={cn('badge', maintenanceStatus.color)}>
                {maintenanceStatus.text}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-dark-400">Owner</p>
                <p className="text-white">{car.owner.name}</p>
              </div>
              <div>
                <p className="text-sm text-dark-400">Phone</p>
                <p className="text-white">{car.owner.phone}</p>
              </div>
              <div>
                <p className="text-sm text-dark-400">Color</p>
                <p className="text-white">{car.color || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-dark-400">Mileage</p>
                <p className="text-white">{car.mileage?.toLocaleString() || 0} km</p>
              </div>
              {car.engineType && (
                <div>
                  <p className="text-sm text-dark-400">Engine</p>
                  <p className="text-white capitalize">{car.engineType} {car.engineCapacity}</p>
                </div>
              )}
              {car.firma && (
                <div>
                  <p className="text-sm text-dark-400">Company</p>
                  <p className="text-white">{car.firma}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-dark-600">
        <nav className="flex space-x-8">
          {['overview', 'documents', 'work-logs'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-dark-400 hover:text-dark-300 hover:border-dark-300'
              )}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'documents' && 'Documents'}
              {tab === 'work-logs' && 'Work Logs'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Technical Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-dark-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Technical Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-dark-400">Transmission</p>
                  <p className="text-white capitalize">{car.transmission || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-dark-400">Drive Type</p>
                  <p className="text-white uppercase">{car.driveType || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-dark-400">Body Type</p>
                  <p className="text-white capitalize">{car.bodyType || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-dark-400">Fuel Capacity</p>
                  <p className="text-white">{car.fuelCapacity ? `${car.fuelCapacity}L` : 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-dark-400">Seating</p>
                  <p className="text-white">{car.seatingCapacity || 5} seats</p>
                </div>
                {car.reifengroesse && (
                  <div>
                    <p className="text-sm text-dark-400">Tire Size</p>
                    <p className="text-white">{car.reifengroesse}</p>
                  </div>
                )}
              </div>
            </div>

            {/* German Details */}
            {(car.fahrer || car.schluesselnummer || car.fahrgestellnummer) && (
              <div className="bg-dark-800 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-4">German Vehicle Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {car.fahrer && (
                    <div>
                      <p className="text-sm text-dark-400">Fahrer</p>
                      <p className="text-white">{car.fahrer}</p>
                    </div>
                  )}
                  {car.schluesselnummer && (
                    <div>
                      <p className="text-sm text-dark-400">Schlüsselnummer</p>
                      <p className="text-white">{car.schluesselnummer}</p>
                    </div>
                  )}
                  {car.fahrgestellnummer && (
                    <div>
                      <p className="text-sm text-dark-400">Fahrgestellnummer</p>
                      <p className="text-white">{car.fahrgestellnummer}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Stats and Dates */}
          <div className="space-y-6">
            {/* Work Stats */}
            <div className="bg-dark-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Work Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-dark-400">Total Work Logs</span>
                  <span className="text-white">{workLogsSummary.totalLogs || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-dark-400">Total Hours</span>
                  <span className="text-white">{workLogsSummary.totalHours || 0}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-dark-400">Total Cost</span>
                  <span className="text-white">${workLogsSummary.totalCost || 0}</span>
                </div>
                {workLogsSummary.lastWorkDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-dark-400">Last Work</span>
                    <span className="text-white">{formatDistanceToNow(workLogsSummary.lastWorkDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Important Dates */}
            <div className="bg-dark-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Important Dates</h3>
              <div className="space-y-3">
                {car.registrationDate && (
                  <div>
                    <p className="text-sm text-dark-400">Registration</p>
                    <p className="text-white">{formatDate(car.registrationDate)}</p>
                  </div>
                )}
                {car.insuranceExpiryDate && (
                  <div>
                    <p className="text-sm text-dark-400">Insurance Expiry</p>
                    <p className="text-white">{formatDate(car.insuranceExpiryDate)}</p>
                  </div>
                )}
                {car.roadTaxExpiryDate && (
                  <div>
                    <p className="text-sm text-dark-400">Road Tax Expiry</p>
                    <p className="text-white">{formatDate(car.roadTaxExpiryDate)}</p>
                  </div>
                )}
                {car.motExpiryDate && (
                  <div>
                    <p className="text-sm text-dark-400">MOT Expiry</p>
                    <p className="text-white">{formatDate(car.motExpiryDate)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Upload Documents */}
          <div className="bg-dark-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Upload Documents</h3>
            
            <div className="space-y-4">
              <div>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="document-upload"
                />
                <label
                  htmlFor="document-upload"
                  className="cursor-pointer border-2 border-dashed border-dark-600 rounded-lg p-6 text-center hover:border-primary-500 transition-colors block"
                >
                  <DocumentArrowUpIcon className="h-12 w-12 text-dark-400 mx-auto mb-2" />
                  <p className="text-dark-300">Click to select documents</p>
                  <p className="text-xs text-dark-400">PDF, DOC, DOCX, JPG, PNG (Max: 10MB each)</p>
                </label>
              </div>

              {/* Document Metadata */}
              {selectedFiles.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Document Details</h4>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="bg-dark-700 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm text-dark-400 mb-1">Name</label>
                          <input
                            type="text"
                            value={documentMetadata[index]?.name || file.name}
                            onChange={(e) => updateDocumentMetadata(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-dark-400 mb-1">Type</label>
                          <select
                            value={documentMetadata[index]?.type || 'other'}
                            onChange={(e) => updateDocumentMetadata(index, 'type', e.target.value)}
                            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                          >
                            {documentTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-dark-400 mb-1">Description</label>
                          <input
                            type="text"
                            value={documentMetadata[index]?.description || ''}
                            onChange={(e) => updateDocumentMetadata(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                            placeholder="Optional description"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={uploadDocuments}
                    disabled={uploadingDocs}
                    className="btn btn-primary"
                  >
                    {uploadingDocs ? 'Uploading...' : `Upload ${selectedFiles.length} Document(s)`}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Existing Documents */}
          <div className="bg-dark-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Documents ({documents.length})</h3>
            
            {documents.length === 0 ? (
              <p className="text-dark-400 text-center py-8">No documents uploaded yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map(document => (
                  <div key={document._id} className="bg-dark-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-8 w-8 text-primary-400 mr-3" />
                        <div>
                          <h4 className="font-medium text-white">{document.name}</h4>
                          <p className="text-xs text-dark-400 capitalize">{document.type}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteDocument(document._id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {document.description && (
                      <p className="text-sm text-dark-300 mb-3">{document.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-dark-400">
                      <span>
                        {document.fileSize && `${(document.fileSize / 1024 / 1024).toFixed(2)}MB`}
                      </span>
                      <span>{formatDistanceToNow(document.uploadedAt)}</span>
                    </div>
                    
                    <a
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 btn btn-sm btn-secondary w-full"
                    >
                      View Document
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'work-logs' && (
        <div className="space-y-6">
          {/* Add Work Log */}
          <div className="bg-dark-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Work Logs</h3>
              <button
                onClick={() => setShowAddWorkLog(!showAddWorkLog)}
                className="btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Work Log
              </button>
            </div>

            {showAddWorkLog && (
              <div className="mb-6 bg-dark-700 rounded-lg p-4">
                <h4 className="font-medium text-white mb-4">New Work Log</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-dark-400 mb-1">Work Type</label>
                    <select
                      value={newWorkLog.workType}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, workType: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                    >
                      {workTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-dark-400 mb-1">Hours Worked</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={newWorkLog.hoursWorked}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, hoursWorked: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-dark-400 mb-1">Labor Cost ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newWorkLog.laborCost}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, laborCost: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-dark-400 mb-1">Total Cost ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newWorkLog.totalCost}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, totalCost: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-dark-400 mb-1">Description *</label>
                    <textarea
                      value={newWorkLog.description}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                      rows={3}
                      placeholder="Describe the work performed..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-dark-400 mb-1">Notes</label>
                    <textarea
                      value={newWorkLog.notes}
                      onChange={(e) => setNewWorkLog(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white"
                      rows={2}
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-3 mt-4">
                  <button onClick={addWorkLog} className="btn btn-primary">
                    Add Work Log
                  </button>
                  <button 
                    onClick={() => setShowAddWorkLog(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Work Logs List */}
          <div className="space-y-4">
            {workLogs.length === 0 ? (
              <div className="bg-dark-800 rounded-lg p-6 text-center">
                <ClipboardDocumentListIcon className="h-12 w-12 text-dark-400 mx-auto mb-4" />
                <p className="text-dark-400">No work logs recorded yet</p>
              </div>
            ) : (
              workLogs.map(log => (
                <div key={log._id} className="bg-dark-800 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={cn(
                          'px-2 py-1 rounded text-xs font-medium',
                          log.workType === 'repair' ? 'bg-red-100 text-red-800' :
                          log.workType === 'service' ? 'bg-blue-100 text-blue-800' :
                          log.workType === 'maintenance' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        )}>
                          {log.workType.charAt(0).toUpperCase() + log.workType.slice(1)}
                        </span>
                        {log.task && (
                          <span className="text-xs text-dark-400">Task-related</span>
                        )}
                      </div>
                      <h4 className="font-medium text-white">{log.description}</h4>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-dark-400">
                        <span className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          {log.mechanic?.name || 'Unknown'}
                        </span>
                        <span className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          {formatDate(log.date)}
                        </span>
                        {log.hoursWorked > 0 && (
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {log.hoursWorked}h
                          </span>
                        )}
                        {log.totalCost > 0 && (
                          <span className="flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            ${log.totalCost}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {log.notes && (
                    <p className="text-dark-300 text-sm mt-3">{log.notes}</p>
                  )}
                  
                  {log.partsUsed && log.partsUsed.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-dark-400 mb-1">Parts Used:</p>
                      <ul className="text-sm text-dark-300">
                        {log.partsUsed.map((part, index) => (
                          <li key={index}>
                            {part.name} x{part.quantity} {part.cost > 0 && `($${part.cost})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Car Form Modal */}
      <CarForm
        isOpen={isCarFormOpen}
        onClose={() => setIsCarFormOpen(false)}
        car={car}
        onCarSaved={handleCarSaved}
      />
    </div>
  );
};

export default CarDetails;